import React from "react";
import { useEnhancedFilter } from "./Hooks";
import { Box, Button, List, Typography } from "@mui/material";
import type { IFilterOption } from "Common/Interfaces";
import {
  FilterButton,
  FilterByButton,
  FilterCheckboxItem,
  FilterDatePresetRangePicker,
  FilterDateTimeRangePicker,
  FilterPanel,
  SearchBox,
  isDateRangeValueValid,
} from "Common/Components";
import { FILTER_OPTION_TYPE } from "Common/Constants";

interface IEnhancedFilter {
  filterState: IFilterOption[];
  tooltip?: string;
  disabled?: boolean;
  handleUpdateFilterState: (filterState: IFilterOption[]) => void;
}
export const EnhancedFilter: React.FC<IEnhancedFilter> = (props) => {
  const { filterState, tooltip, handleUpdateFilterState, disabled } = props;
  const [isDatePresetRangeValid, setIsDatePresetRangeValid] = React.useState(true);

  const isDatePresetRangeValueValid = (filterOption?: IFilterOption | null) => {
    const preset = filterOption?.datePresetRangeValues;
    if (filterOption?.type !== FILTER_OPTION_TYPE.DATE_PRESET_RANGE || preset?.selectedPreset !== "custom") return true;
    return isDateRangeValueValid(preset.customStartDate, preset.customEndDate);
  };

  const isEmptyCustomDatePreset = (filterOption?: IFilterOption | null) =>
    filterOption?.type === FILTER_OPTION_TYPE.DATE_PRESET_RANGE &&
    filterOption.datePresetRangeValues?.selectedPreset === "custom" &&
    !filterOption.datePresetRangeValues.customStartDate &&
    !filterOption.datePresetRangeValues.customEndDate;

  const {
    filterMenu,
    tempFilterState,
    searchText,
    selectedFilterOption,
    hasDateRangeError,
    handleToggle,
    handleClose,
    handleSearchChange,
    getFilterValuesBasedOnSearch,
    handleCheckboxChange,
    handleSetSelectedFilterOption,
    handleDateRangeSelect,
    handleDatePresetRangeUpdate,
  } = useEnhancedFilter({
    currentFilterState: filterState,
  });

  const handleApplyFilters = (e: React.MouseEvent<HTMLButtonElement>) => {
    const activeFilterOption = selectedFilterOption
      ? (tempFilterState.find((filterOption) => filterOption.id === selectedFilterOption.id) ?? selectedFilterOption)
      : null;

    if (!isDatePresetRangeValueValid(activeFilterOption) || !isDatePresetRangeValid) {
      return;
    }

    if (isEmptyCustomDatePreset(activeFilterOption)) {
      handleClose(e);
      return;
    }

    if (hasDateRangeError) {
      const filteredState = tempFilterState.map((filterOption) => {
        if (filterOption.type === FILTER_OPTION_TYPE.DATE_RANGE && filterOption.dateRangeValues) {
          return {
            ...filterOption,
            dateRangeValues: {
              ...filterOption.dateRangeValues,
              startDate: null,
              endDate: null,
            },
          };
        }
        return filterOption;
      });
      handleUpdateFilterState(filteredState);
    } else {
      handleUpdateFilterState(tempFilterState);
    }
    handleClose(e);
  };

  return (
    <React.Fragment>
      <FilterButton
        onClick={handleToggle}
        tooltip={tooltip}
        aria-controls={filterMenu.open ? "filter-menu" : undefined}
        aria-expanded={filterMenu.open ? "true" : undefined}
        aria-haspopup="true"
        aria-label="Filter"
        disabled={disabled}
      />
      <FilterPanel open={filterMenu.open} anchorEl={filterMenu.anchorEl} onClose={handleClose}>
        <Box
          sx={(theme) => ({
            background: theme.palette.grey[100],
            padding: 2,
            [theme.breakpoints.up("sm")]: {
              width: "200px",
              minWidth: "200px",
              maxWidth: "200px",
            },
            [theme.breakpoints.down("sm")]: {
              width: "100%",
              height: "200px",
            },
          })}
        >
          <List>
            {tempFilterState.map((filterOption) => (
              <FilterByButton
                key={filterOption.id}
                label={filterOption.label}
                tooltip={filterOption.tooltip}
                hasAppliedValues={
                  (filterOption.checkboxValues?.some((value) => value.isChecked) ||
                    (filterOption.autoCompleteValues?.value !== null &&
                      filterOption.autoCompleteValues?.value !== undefined) ||
                    (filterOption.dateRangeValues?.startDate !== null &&
                      filterOption.dateRangeValues?.startDate !== undefined) ||
                    (filterOption.dateRangeValues?.endDate !== null &&
                      filterOption.dateRangeValues?.endDate !== undefined) ||
                    (filterOption.autoCompleteWithCheckboxValues?.value !== null &&
                      filterOption.autoCompleteWithCheckboxValues?.value !== undefined) ||
                    filterOption.autoCompleteWithCheckboxValues?.checkboxValue?.isChecked ||
                    (filterOption.datePresetRangeValues?.selectedPreset !== null &&
                      filterOption.datePresetRangeValues?.selectedPreset !== undefined)) ??
                  false
                }
                selected={selectedFilterOption?.id === filterOption.id}
                onClick={() => handleSetSelectedFilterOption(filterOption)}
              />
            ))}
          </List>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, p: 2, overflow: "hidden" }}>
            {selectedFilterOption?.hasSearch && (
              <SearchBox
                disableDebouncedSearch
                hasBorder
                value={searchText[selectedFilterOption.id] ?? ""}
                handleSearch={(search) => handleSearchChange(selectedFilterOption.id, search)}
                handleClear={() => handleSearchChange(selectedFilterOption.id, "")}
                sx={{ mb: 2 }}
              />
            )}

            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "auto" }}>
              {getFilterValuesBasedOnSearch(selectedFilterOption?.id ?? 0)?.length === 0 &&
                selectedFilterOption?.type === FILTER_OPTION_TYPE.CHECKBOX && (
                  <Box
                    sx={{
                      display: "flex",
                      flexGrow: 1,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography color="textSecondary">No Options</Typography>
                  </Box>
                )}

              {selectedFilterOption?.type === FILTER_OPTION_TYPE.CHECKBOX && (
                <List component="div">
                  {getFilterValuesBasedOnSearch(selectedFilterOption.id).map((filterValue) => (
                    <FilterCheckboxItem
                      key={filterValue.value.toString()}
                      label={filterValue.label}
                      checked={filterValue.isChecked ?? false}
                      disabled={filterValue.handleDisability?.(tempFilterState) ?? filterValue.isDisabled}
                      onChange={() => handleCheckboxChange(selectedFilterOption.id, filterValue.value)}
                    />
                  ))}
                </List>
              )}
              {selectedFilterOption?.type === FILTER_OPTION_TYPE.DATE_TIME_RANGE && (
                <FilterDateTimeRangePicker
                  key={selectedFilterOption.id}
                  startDate={
                    tempFilterState.find((temp) => temp.id === selectedFilterOption.id)?.dateRangeValues?.startDate ??
                    selectedFilterOption?.dateRangeValues?.startDate ??
                    null
                  }
                  endDate={
                    tempFilterState.find((temp) => temp.id === selectedFilterOption.id)?.dateRangeValues?.endDate ??
                    selectedFilterOption?.dateRangeValues?.endDate ??
                    null
                  }
                  onChangeDate={(date, position) =>
                    handleDateRangeSelect(selectedFilterOption.id, date ?? null, position, false)
                  }
                />
              )}
              {selectedFilterOption?.type === FILTER_OPTION_TYPE.DATE_PRESET_RANGE &&
                selectedFilterOption.datePresetRangeValues && (
                  <FilterDatePresetRangePicker
                    key={selectedFilterOption.id}
                    value={
                      tempFilterState.find((temp) => temp.id === selectedFilterOption.id)?.datePresetRangeValues ??
                      selectedFilterOption.datePresetRangeValues ?? {
                        selectedPreset: null,
                        customStartDate: null,
                        customEndDate: null,
                      }
                    }
                    onChangePreset={(preset) => {
                      if (preset === "custom") {
                        handleDatePresetRangeUpdate(selectedFilterOption.id, { selectedPreset: preset });
                        return;
                      }

                      handleDatePresetRangeUpdate(selectedFilterOption.id, {
                        selectedPreset: preset,
                        customStartDate: null,
                        customEndDate: null,
                      });
                    }}
                    onChangeCustomDate={(date, position) =>
                      handleDatePresetRangeUpdate(selectedFilterOption.id, { [position]: date })
                    }
                    onValidationChange={setIsDatePresetRangeValid}
                  />
                )}
            </Box>
          </Box>
          <Box
            sx={(theme) => ({
              padding: 2,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              borderTop: `1px solid ${theme.palette.divider}`,
              gap: 1,
            })}
          >
            <Button id="filter-panel-clear-button" variant="text" onClick={handleClose}>
              Cancel
            </Button>
            <Button id="filter-panel-apply-button" onClick={handleApplyFilters}>
              Apply
            </Button>
          </Box>
        </Box>
      </FilterPanel>
    </React.Fragment>
  );
};
